import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ToolCard from './components/ToolCard';
import ToolsPage from './components/ToolsPage';
import BlogOverview from './components/BlogOverview';
import BlogPost from './components/BlogPost';
import ReviewsPage from './components/ReviewsPage';
import ReviewPage from './components/ReviewPage';
import TutorialsPage from './components/TutorialsPage';
import TutorialPage from './components/TutorialPage';
import FirstClientGuaranteedPage from './components/FirstClientGuaranteedPage';
import ErsterKundeGarantiertPage from './components/ErsterKundeGarantiertPage';
import ImprintPage from './components/ImprintPage';
import PrivacyPage from './components/PrivacyPage';
import Footer from './components/Footer';
import SchemaMarkup from './components/SchemaMarkup';
import { toolsData } from './data/toolsData';
import { reviewsData } from './data/reviewsData';
import { tutorialsData } from './data/tutorialsData';
import './App.css';

function App() {
  const location = useLocation();

  const featuredTools = toolsData.slice(0, 4);
  const totalReviews = reviewsData.length;
  const averageRating = (reviewsData.reduce((sum, review) => sum + review.rating, 0) / totalReviews).toFixed(1);
  const totalTutorials = tutorialsData.length;
  const totalTutorialDuration = tutorialsData.reduce((total, tutorial) => {
    const minutes = parseInt(tutorial.duration.match(/\d+/)[0]);
    return total + minutes;
  }, 0);
  
  const formatTotalDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 ? `${hours}h ${remainingMinutes}m` : `${remainingMinutes}m`;
  };

  const SharedHeader = () => (
    <header className="header">
      <div className="container">
        <motion.div 
          className="logo"
          initial={{ opacity: 0, y: -50, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, type: "spring", stiffness: 100, damping: 10 }}
        >
          <motion.div 
            className="alien-icon"
            whileHover={{ scale: 1.1, rotate: [0, -10, 10, -10, 0], transition: { duration: 0.5 } }}
          >
            👽
          </motion.div>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div style={{
              fontSize: '42px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #00ff88 0%, #00ccff 50%, #ff00ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Extraterrestrial Tools</div>
          </Link>
        </motion.div>
        
        <motion.p 
          className="tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Discover the Best AI Software & Exclusive Lifetime Deals
        </motion.p>
      </div>
    </header>
  );

  const LandingPage = () => (
    <>
      <main className="main">
        <section className="hero-section">
          <div className="container">
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <h1 style={{
                fontSize: '32px',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #64ffda 0%, #00ff88 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '1.5rem',
                textAlign: 'center',
                backgroundColor: 'initial'
              }}>Discover AI Software & Lifetime Deals That Actually Work</h1>
              
              <p style={{
                fontSize: '1.3rem',
                color: '#b0b0b0',
                textAlign: 'center',
                maxWidth: '600px',
                margin: '0 auto 3rem',
                lineHeight: '1.6'
              }}>
                I test hundreds of AI tools so you don't have to. Steal the tech stack entrepreneurs use to scale their business with massive ROI.
              </p>

              <motion.div
                className="cta-container"
                style={{ textAlign: 'center' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.a
                  href="https://sendfox.com/lp/1y867e"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{
                    fontSize: '1.2rem',
                    padding: '1rem 2.5rem',
                    background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                    border: 'none',
                    borderRadius: '50px',
                    color: '#000',
                    textDecoration: 'none',
                    display: 'inline-block',
                    fontWeight: '700',
                    boxShadow: '0 10px 30px rgba(255, 193, 7, 0.4)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}
                >
                  💰 Get SaaS Slayer's Toolkit
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <div className="container">
          <motion.section
            className="section-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              borderRadius: '20px',
              padding: '3rem',
              marginBottom: '4rem',
              cursor: 'pointer'
            }}
          >
            <Link to="/tools" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem', color: '#64ffda' }}>
                  🛸 Software Directory
                </h3>
                <p style={{ fontSize: '1.2rem', color: '#b0b0b0', marginBottom: '2rem' }}>
                  {toolsData.length} handpicked AI tools and productivity software across multiple categories
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1.5rem',
                marginTop: '2rem'
              }}>
                {featuredTools.map((tool, index) => (
                  <motion.div
                    key={tool.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <ToolCard tool={tool} />
                  </motion.div>
                ))}
              </div>

              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <span style={{ color: '#64ffda', fontSize: '1.1rem', fontWeight: '600' }}>
                  Browse All {toolsData.length} Tools & Deals →
                </span>
              </div>
            </Link>
          </motion.section>

          <motion.section
            className="section-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{
              background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(100, 255, 218, 0.1) 100%)',
              border: '1px solid rgba(0, 255, 136, 0.2)',
              borderRadius: '20px',
              padding: '3rem',
              marginBottom: '4rem',
              cursor: 'pointer'
            }}
          >
            <Link to="/reviews" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem', color: '#00ff88' }}>
                  🎥 AI Software Reviews
                </h3>
                <p style={{ fontSize: '1.2rem', color: '#b0b0b0', marginBottom: '2rem' }}>
                  In-depth video reviews of the latest AI software. Real testing, honest results.
                </p>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '2rem',
                  fontSize: '1.1rem',
                  color: '#00ff88',
                  fontWeight: '600'
                }}>
                  <span>⭐ {averageRating} Rating</span>
                  <span>•</span>
                  <span>📺 {totalReviews} Reviews</span>
                </div>
              </div>
            </Link>
          </motion.section>

          <motion.section
            className="saas-toolkit-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
              border: '2px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '20px',
              padding: '4rem 3rem',
              marginBottom: '4rem',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h3 style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '1rem', color: '#ffc107' }}>
                The SaaS Slayer's Toolkit
              </h3>
              <p style={{ fontSize: '1.3rem', color: '#fff', fontWeight: '600', marginBottom: '1.5rem' }}>
                Save $3,700+ With The Best Software Lifetime Deals
              </p>
              <p style={{ fontSize: '1.1rem', color: '#e0e0e0', marginBottom: '2.5rem', maxWidth: '700px', margin: '0 auto 2.5rem', lineHeight: '1.6' }}>
                Stop paying monthly. Steal the exact lifetime-deal tech stack that replaces Kajabi, Jasper, and Calendly. Achieve a <strong style={{ color: '#ffc107' }}>4,236% ROI</strong>.
              </p>
              <motion.a
                href="https://sendfox.com/lp/1y867e"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  padding: '1.2rem 3rem',
                  background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                  color: '#000',
                  textDecoration: 'none',
                  borderRadius: '50px',
                }}
              >
                🚀 Get The Toolkit FREE
              </motion.a>
            </div>
          </motion.section>

          <motion.section
            className="featured-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            style={{
              background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(255, 142, 83, 0.1) 100%)',
              border: '1px solid rgba(255, 107, 107, 0.2)',
              borderRadius: '20px',
              padding: '3rem',
              marginBottom: '4rem',
              textAlign: 'center'
            }}
          >
            <h3 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: '#ff6b6b' }}>
              🎯 Exclusive Lifetime Deals
            </h3>
            <p style={{ fontSize: '1.2rem', color: '#b0b0b0', marginBottom: '2rem' }}>
              Save thousands with one-time payments on premium AI software and tools
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
              <motion.div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '15px', padding: '2rem', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <h4 style={{ color: '#ff6b6b', fontSize: '1.3rem', marginBottom: '1rem' }}>🛒 Best Software Deals</h4>
                <p style={{ color: '#b0b0b0', marginBottom: '1rem' }}>One-time payment software alternatives to expensive monthly subscriptions.</p>
                <Link to="/tools" style={{ color: '#ff6b6b', textDecoration: 'none', fontWeight: '600' }}>Browse Deals →</Link>
              </motion.div>

              <motion.div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '15px', padding: '2rem', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <h4 style={{ color: '#ff6b6b', fontSize: '1.3rem', marginBottom: '1rem' }}>🎓 Master AI Tools</h4>
                <p style={{ color: '#b0b0b0', marginBottom: '1rem' }}>Step-by-step guides to automate your business with the latest AI software.</p>
                <Link to="/tutorials" style={{ color: '#ff6b6b', textDecoration: 'none', fontWeight: '600' }}>Start Learning →</Link>
              </motion.div>
            </div>
          </motion.section>
        </div>
      </main>
    </>
  );

  return (
    <div className="app">
      <div className="stars"></div>
      <SchemaMarkup />
      <SharedHeader />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/blog" element={<main className="main"><div className="container"><BlogOverview /></div></main>} />
        <Route path="/blog/:slug" element={<main className="main"><div className="container"><BlogPost /></div></main>} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/reviews/:slug" element={<ReviewPage />} />
        <Route path="/tutorials" element={<TutorialsPage />} />
        <Route path="/tutorials/:slug" element={<TutorialPage />} />
        <Route path="/first-client-guaranteed" element={<FirstClientGuaranteedPage />} />
        <Route path="/erster-kunde-garantiert" element={<ErsterKundeGarantiertPage />} />
        <Route path="/imprint" element={<ImprintPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default App;